import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Plus,
  FolderOpen,
  Users,
  Database,
  Clock,
  MoreHorizontal,
  Settings,
  Trash2,
  Share2,
  UserPlus,
  Globe,
  Lock,
  Building2,
  Users2,
  Atom,
  Leaf,
  Heart,
  Flame,
  Sparkles,
  Beaker,
  Microscope,
  Brain,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { Workspace, AccessLevel } from '@/types/research';
import { cn } from '@/lib/utils';

const WORKSPACE_ICONS: Record<string, React.ReactNode> = {
  Globe: <Globe className="h-6 w-6" />,
  Atom: <Atom className="h-6 w-6" />,
  Leaf: <Leaf className="h-6 w-6" />,
  Heart: <Heart className="h-6 w-6" />,
  Flame: <Flame className="h-6 w-6" />,
  Sparkles: <Sparkles className="h-6 w-6" />,
  Beaker: <Beaker className="h-6 w-6" />,
  Microscope: <Microscope className="h-6 w-6" />,
  Brain: <Brain className="h-6 w-6" />,
};

const ACCESS_LEVEL_CONFIG: Record<AccessLevel, { icon: React.ReactNode; label: string; description: string }> = {
  private: { icon: <Lock className="h-4 w-4" />, label: 'Private', description: 'Only you can access' },
  team: { icon: <Users2 className="h-4 w-4" />, label: 'Team', description: 'Your team members' },
  department: { icon: <Building2 className="h-4 w-4" />, label: 'Department', description: 'Your department' },
  institution: { icon: <Building2 className="h-4 w-4" />, label: 'Institution', description: 'Your institution' },
  public: { icon: <Globe className="h-4 w-4" />, label: 'Public', description: 'Anyone can view' },
};

const AVAILABLE_COLORS = [
  '#667eea', '#764ba2', '#48bb78', '#ed8936', '#f687b3',
  '#4299e1', '#9f7aea', '#38b2ac', '#e53e3e', '#dd6b20',
];

export function ResearchWorkspacesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['research-workspaces'],
    queryFn: () => researchService.listWorkspaces(),
  });

  const workspaces = data?.data || [];

  const filteredWorkspaces = workspaces.filter((ws) =>
    ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ws.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return formatDate(dateString);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Research Workspaces</h1>
          <p className="text-muted-foreground">
            Organize resources into project-specific collections and collaborate with your team
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Workspace
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search workspaces..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Workspaces Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-14 w-14 rounded-xl" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-32 mb-2" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredWorkspaces.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-12 pb-12 text-center">
            <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No workspaces found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery
                ? 'Try a different search term'
                : 'Create your first workspace to organize research resources'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Workspace
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredWorkspaces.map((workspace) => {
            const accessConfig = ACCESS_LEVEL_CONFIG[workspace.accessLevel];

            return (
              <Card
                key={workspace.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50 group"
                onClick={() => setSelectedWorkspace(workspace)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div
                      className="flex h-14 w-14 items-center justify-center rounded-xl text-white"
                      style={{ backgroundColor: workspace.color }}
                    >
                      {WORKSPACE_ICONS[workspace.iconName] || <FolderOpen className="h-6 w-6" />}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Settings className="mr-2 h-4 w-4" />
                          Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <UserPlus className="mr-2 h-4 w-4" />
                          Invite Members
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Share2 className="mr-2 h-4 w-4" />
                          Share
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardTitle className="text-lg leading-tight mt-3">{workspace.name}</CardTitle>
                  <CardDescription className="line-clamp-2">{workspace.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  {/* Owner */}
                  <div className="flex items-center gap-2 mb-4">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs bg-primary/10">
                        {workspace.ownerName.split(' ').map((n) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-muted-foreground">Created by {workspace.ownerName}</span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {workspace.memberCount} members
                    </span>
                    <span className="flex items-center gap-1">
                      <Database className="h-4 w-4" />
                      {workspace.resourceCount} resources
                    </span>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <Badge variant="outline" className="text-xs">
                      {accessConfig.icon}
                      <span className="ml-1">{accessConfig.label}</span>
                    </Badge>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(workspace.lastActivityAt)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Workspace Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
            <DialogDescription>
              Create a project-specific collection to organize research resources and collaborate with your team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Workspace Name</Label>
              <Input placeholder="e.g., Climate Research 2024" />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea placeholder="Describe the purpose of this workspace..." rows={3} />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_COLORS.map((color) => (
                    <button
                      key={color}
                      className="h-8 w-8 rounded-full border-2 border-transparent hover:border-primary transition-colors"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(WORKSPACE_ICONS).slice(0, 6).map(([name, icon]) => (
                    <button
                      key={name}
                      className="h-10 w-10 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center text-muted-foreground hover:text-primary"
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Access Level</Label>
              <Select defaultValue="team">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCESS_LEVEL_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {config.icon}
                        <div>
                          <span className="font-medium">{config.label}</span>
                          <span className="text-muted-foreground"> - {config.description}</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workspace Detail Dialog */}
      <Dialog open={!!selectedWorkspace} onOpenChange={() => setSelectedWorkspace(null)}>
        <DialogContent className="max-w-2xl">
          {selectedWorkspace && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <div
                    className="flex h-16 w-16 items-center justify-center rounded-xl text-white flex-shrink-0"
                    style={{ backgroundColor: selectedWorkspace.color }}
                  >
                    {WORKSPACE_ICONS[selectedWorkspace.iconName] || <FolderOpen className="h-8 w-8" />}
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-xl">{selectedWorkspace.name}</DialogTitle>
                    <DialogDescription className="mt-1">
                      {selectedWorkspace.description}
                    </DialogDescription>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">
                        {ACCESS_LEVEL_CONFIG[selectedWorkspace.accessLevel].icon}
                        <span className="ml-1">{ACCESS_LEVEL_CONFIG[selectedWorkspace.accessLevel].label}</span>
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Created {formatDate(selectedWorkspace.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-6">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <Users className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                      <p className="text-2xl font-bold">{selectedWorkspace.memberCount}</p>
                      <p className="text-xs text-muted-foreground">Members</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <Database className="h-6 w-6 mx-auto text-green-500 mb-2" />
                      <p className="text-2xl font-bold">{selectedWorkspace.resourceCount}</p>
                      <p className="text-xs text-muted-foreground">Resources</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <Clock className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                      <p className="text-lg font-bold">{formatRelativeTime(selectedWorkspace.lastActivityAt)}</p>
                      <p className="text-xs text-muted-foreground">Last Activity</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Owner */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Workspace Owner</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback className="bg-primary/10">
                          {selectedWorkspace.ownerName.split(' ').map((n) => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{selectedWorkspace.ownerName}</p>
                        <p className="text-sm text-muted-foreground">Owner</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>• Resource added: "Climate Data Analysis Report" - 2 hours ago</p>
                      <p>• New member joined: Dr. Sarah Mitchell - Yesterday</p>
                      <p>• Workspace settings updated - 3 days ago</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedWorkspace(null)}>
                  Close
                </Button>
                <Button variant="outline">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite Members
                </Button>
                <Button>
                  <Database className="mr-2 h-4 w-4" />
                  View Resources
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
